import {executeAiAgentProposal} from '../../../redux/aiAgent/aiAgentActions';
import {navigateAfterExecute} from './navigateAfterExecute';
import {mergeCurrentFormValues} from './agentFormValues';
import {
    isRepeatOfCreatedDraft,
    rememberCreatedDraft,
    rememberProposalContext,
} from './agentSessionMemory';
import type {NavigateFn} from './navigateAfterExecute';
import type {ExecuteAiAgentProposalParams} from '../../../redux/aiAgent/aiAgentActions';
import type {AiAgentPendingProposal} from '../../../redux/aiAgent/aiAgent.type';
import type {DraftResult} from '../../aiMessageGenerator/utils/types';

export type AiAgentProposalOptions = {
    actionFailed: string;
    createdOpenFailed: string;
};

export type RunAgentProposalStatus = 'executed' | 'repeat' | 'failed' | 'openFailed';

export type RunAgentProposalResult = DraftResult & {
    status: RunAgentProposalStatus;
};

type RunAgentProposalParams = {
    proposal: AiAgentPendingProposal;
    agentText: string;
    execute: Omit<ExecuteAiAgentProposalParams, 'proposal'>;
    navigate: NavigateFn;
    options: AiAgentProposalOptions;
};

/** The agent's own wording stays, with the notice under it. */
function withAgentText(agentText: string, notice: string): string {
    return agentText ? `${agentText}\n\n${notice}` : notice;
}

/**
 * The turn created nothing, so the values it collected have to stay known — the next turn
 * sends only the change, and the record has to be complete enough to create.
 */
function keepProposalFields(
    proposal: AiAgentPendingProposal,
    text: string,
    status: Exclude<RunAgentProposalStatus, 'executed' | 'openFailed'>,
): RunAgentProposalResult {
    mergeCurrentFormValues(proposal.fields);
    return {text, status};
}

export async function runAgentProposal({
    proposal,
    agentText,
    execute,
    navigate,
    options,
}: RunAgentProposalParams): Promise<RunAgentProposalResult> {
    rememberProposalContext(proposal);

    if(isRepeatOfCreatedDraft(proposal)) {
        return {text: agentText, status: 'repeat'};
    }

    // The request runs with handleError, so a 400/500 rejects instead of resolving null —
    // both mean the same here, and an escaping rejection would drop the turn out of the chat
    // instead of answering with the failure.
    const result = await executeAiAgentProposal({...execute, proposal}).catch(() => null);
    if(!result) {
        return keepProposalFields(proposal, withAgentText(agentText, options.actionFailed), 'failed');
    }

    let openFailed = false;
    try {
        await navigateAfterExecute(result, navigate);
    } catch {
        openFailed = true;
    }
    // After the wizard reset, so the created values are the ones the chat keeps. Also runs
    // when opening failed: the draft exists, and a next turn repeating the same values must
    // read as a repeat instead of creating a second one.
    rememberCreatedDraft(proposal.fields);

    if(openFailed) {
        return {text: withAgentText(agentText, options.createdOpenFailed), status: 'openFailed'};
    }
    return {text: agentText, status: 'executed'};
}
